import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, Stack, TextField, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import { QRCodeSVG } from 'qrcode.react';
import { CabecalhoModal } from './cabecalho-modal';
import { formatarBRL } from '../lib/formato';
import { montarLinkInstagram, montarLinkWhatsappComTexto } from '../lib/contato';

/** Mensagem pronta do WhatsApp: o cliente só anexa a imagem do comprovante. */
export function mensagemComprovantePix(referencia: string, valor: number): string {
  return `Olá! Segue o comprovante do Pix do pedido #${referencia} (${formatarBRL(valor)}).`;
}

/**
 * Pix direto para a conta da loja (forma LOCAL + PIX com chave). Nada aqui
 * confirma o pagamento: quem confirma é o cliente, mandando o comprovante.
 */
export function DialogPixEstatico({
  aberto,
  aoFechar,
  copiaECola,
  valor,
  referencia,
  whatsapp,
  instagram,
}: {
  aberto: boolean;
  aoFechar: () => void;
  copiaECola: string;
  valor: number;
  referencia: string;
  whatsapp?: string | null;
  instagram?: string | null;
}): JSX.Element {
  const [copiado, setCopiado] = useState(false);
  const temWhatsapp = !!whatsapp?.trim();
  const temInstagram = !!instagram?.trim();

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(copiaECola);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      /* clipboard indisponível — o cliente pode selecionar o texto do campo */
    }
  }

  return (
    <Dialog open={aberto} onClose={aoFechar} fullWidth maxWidth="xs" scroll="body">
      <CabecalhoModal titulo="Pagar com Pix" aoFechar={aoFechar} />
      <DialogContent sx={{ pt: 2.5, pb: 3 }}>
        <Stack spacing={2.5}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Pedido #{referencia}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {formatarBRL(valor)}
            </Typography>
          </Box>

          <Passo numero={1} titulo="Copie o código Pix">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No app do seu banco, escolha <b>Pix copia e cola</b> e cole o código.
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                value={copiaECola}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                inputProps={{ 'aria-label': 'Código Pix copia e cola' }}
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={copiar} variant="contained" startIcon={<ContentCopyIcon />} sx={{ flexShrink: 0 }}>
                {copiado ? 'Copiado' : 'Copiar'}
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Ou, se estiver pagando por outro celular, leia o QR Code:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, lineHeight: 0 }}>
                <QRCodeSVG value={copiaECola} size={180} />
              </Box>
            </Box>
          </Passo>

          <Passo numero={2} titulo="Confira e pague">
            <Typography variant="body2" color="text.secondary">
              Antes de confirmar no banco, veja se o valor é {formatarBRL(valor)} e se o nome é o da loja.
            </Typography>
          </Passo>

          <Passo numero={3} titulo="Envie o comprovante para a loja">
            {temWhatsapp ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  A loja não recebe aviso automático deste Pix. É o comprovante que confirma o seu pagamento.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={<WhatsAppIcon />}
                  href={montarLinkWhatsappComTexto(whatsapp!, mensagemComprovantePix(referencia, valor))}
                  target="_blank"
                  rel="noopener"
                  aria-label="Enviar comprovante pelo WhatsApp"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Enviar comprovante
                </Button>
              </>
            ) : (
              <>
                <Alert severity="warning" variant="outlined" sx={{ mb: temInstagram ? 1.5 : 0 }}>
                  Esta loja não tem WhatsApp cadastrado. Envie o comprovante pelo Instagram ou por outro
                  contato da loja. <b>Confirmar o pagamento é com você:</b> a loja não recebe aviso
                  automático deste Pix.
                </Alert>
                {temInstagram && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<InstagramIcon />}
                    href={montarLinkInstagram(instagram!)}
                    target="_blank"
                    rel="noopener"
                  >
                    Abrir Instagram da loja
                  </Button>
                )}
              </>
            )}
          </Passo>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function Passo({ numero, titulo, children }: { numero: number; titulo: string; children: ReactNode }): JSX.Element {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          typography: 'body2',
          fontWeight: 700,
        }}
        aria-hidden
      >
        {numero}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography fontWeight={700} sx={{ lineHeight: '26px', mb: 0.5 }}>
          {titulo}
        </Typography>
        {children}
      </Box>
    </Stack>
  );
}
